// mergeTemplates.mjs
//
// PURE tombstone-aware template merge (brief 5.15 §1). Node-testable
// (`node --test src/mergeTemplates.test.mjs`); imported by App.jsx's load path.
//
// The bug it fixes: a template deleted locally whose SERVER delete didn't take (the
// studio had no/expired suite token → serverDeleteTemplate early-returns silently) stays
// in the server blob. On the next load the merge would re-add it as "server-only" → it
// RESURRECTS (the owner sees deleted mockups reappear after running Speedrun, which is
// just what makes him reopen the studio). 5.14 added deletion tombstones to block that,
// but pruned them whenever `server` was empty — including when the server was never
// actually READ (no token → serverLoad returned null → server=[]), so the guard was lost
// and the row resurrected on the next authed load.
//
// The rule here: NEVER prune tombstones / retry deletes unless the server was genuinely
// read (`serverReadOk`). Then, and only then, do we prune tombstones the server has
// already dropped and retry the delete for any the server still has.
//
// Returns:
//   merged        — local ∪ (server-only), MINUS any tombstoned id (never resurrects)
//   addT          — the server-only templates actually added (to persist into IndexedDB)
//   keepTombstones — the tombstone list to persist (unchanged unless serverReadOk)
//   retryDeletes  — ids still on the server to re-issue delete-template for (empty unless serverReadOk)
export function mergeTemplatesWithTombstones({ local = [], server = [], tombstones = [], serverReadOk = false }) {
  const L = Array.isArray(local) ? local : [];
  const S = Array.isArray(server) ? server : [];
  const tombArr = Array.isArray(tombstones) ? tombstones : [];
  const tomb = new Set(tombArr);
  const lIds = new Set(L.map((t) => t && t.id));

  const addT = S.filter((t) => t && t.id != null && !lIds.has(t.id) && !tomb.has(t.id));
  const merged = [...L, ...addT].filter((t) => t && !tomb.has(t.id));

  let keepTombstones = tombArr;
  let retryDeletes = [];
  if (serverReadOk) {
    const srvIds = new Set(S.map((t) => t && t.id));
    keepTombstones = tombArr.filter((id) => srvIds.has(id)); // drop those the server no longer has
    retryDeletes = keepTombstones.slice();                    // still present → the delete didn't take; retry it
  }
  return { merged, addT, keepTombstones, retryDeletes };
}
